/**
 * Stock-Modal back-restore utilities (P2, Round 79 — Feb 12, 2026).
 *
 * Solves the user's ask:
 *   "When a link opens from the Stock Modal in the same tab and the
 *   modal shuts down, on Back press I want the page that originally
 *   showed the modal AND the modal re-opened at that stock."
 *
 * How it works
 * ────────────
 * 1. Inside StockModal, every navigate(...) call that follows an
 *    in-modal link wraps the destination with state:
 *        navigate(toPath, { state: { modalOrigin: { fromPath, ticker } } })
 *    React Router stores user state under `window.history.state.usr`.
 *
 * 2. <BackOrHomeButton /> checks `window.history.state?.usr?.modalOrigin`
 *    on click. When present (and we're in `back` mode), it skips the
 *    default hop-skip math and explicitly navigates to `fromPath`
 *    with `state: { reopenModalTicker }` so the origin page knows to
 *    re-open the modal.
 *
 * 3. Each origin page (Dashboard, Screener, KingsChallenge,
 *    TierChallenge, SectorView, Rebalancer) calls
 *    `useReopenModalOnMount(handleStockClick)` which inspects
 *    `location.state?.reopenModalTicker` on mount, invokes the page's
 *    existing `handleStockClick(ticker)` callback, and clears the
 *    state via `navigate(path, { replace: true })` so reloads /
 *    further back-navs don't loop on it.
 *
 * State key naming
 * ────────────────
 * Two related keys, intentionally separate:
 *   * `modalOrigin` — set on the destination entry (e.g. /triangle/X).
 *     Says "I came from <fromPath>, the modal was open at <ticker>".
 *   * `reopenModalTicker` — set on the ORIGIN entry when the back
 *     button fires. Says "as soon as you mount, reopen the modal at
 *     this ticker."
 *
 * Glossary opens in a NEW TAB (target="_blank"), so it does NOT
 * participate in this flow and is intentionally not patched.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook for in-modal navigation. Returns a navigate-with-modal-origin
 * function. Pass the modal's current ticker in `currentTicker`. The
 * `fromPath` argument is required because the modal is usually
 * mounted ABOVE a page that knows its own canonical path (e.g.
 * `location.pathname + location.search` for Dashboard sub-views).
 */
export function useStockModalNav(currentTicker, fromPath) {
  const navigate = useNavigate();
  return (toPath) => {
    if (!currentTicker || !fromPath) {
      // Defensive — fall back to a plain navigate so a missing arg
      // doesn't break the navigation itself.
      navigate(toPath);
      return;
    }
    navigate(toPath, {
      state: { modalOrigin: { fromPath, ticker: currentTicker } },
    });
  };
}

/**
 * Hook for ORIGIN pages — checks for an inbound "reopen the modal at
 * X" instruction left by <BackOrHomeButton />.
 *
 * Pass your existing `handleStockClick(ticker)` callback. The hook
 * fires it once on mount when the inbound state matches AND clears
 * the state via replaceState so a reload doesn't loop.
 */
export function useReopenModalOnMount(handleStockClick) {
  const location = useLocation();
  const navigate = useNavigate();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    const tkr = location.state?.reopenModalTicker;
    const tab = location.state?.reopenModalTab || null;
    if (typeof tkr === 'string' && tkr.length > 0 && typeof handleStockClick === 'function') {
      firedRef.current = true;
      // Defer to next tick so the page's own onMount fetches finish
      // first. Pages typically clear `selectedStock` on mount; firing
      // immediately would race against that reset.
      setTimeout(() => {
        try { handleStockClick(tkr, tab); } catch { /* swallow — UX recovery */ }
      }, 50);
      // Clear inbound state so a reload / further back-nav doesn't
      // re-trigger. `replace` keeps the URL identical.
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
  }, [location, navigate, handleStockClick]);
}
